/**
 * AdminUserRelationshipsPage - User engagement graph viewer
 * Feature: 080-critique-sharing
 *
 * Displays a user's engagement graph showing all their connections and interactions.
 * Admin-only page for debugging user relationships.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useUserGraphQL } from '../../graphql/hooks/useUserGraphQL';
import {
  RelationshipGraphViewer,
  ConnectionHistory,
} from '../../components/admin-engagement-graph';
import { ConnectionSummary } from '../../graphql/hooks/useEngagementGraphGraphQL';
import { adminUsersUrl, adminUserDetailUrl } from '../../utils/urls';

export function AdminUserRelationshipsPage() {
  const { globalId } = useParams<{ globalId: string }>();
  const { data: userDetail, isLoading: userLoading } = useUserGraphQL(globalId);
  const [selectedConnection, setSelectedConnection] = useState<ConnectionSummary | null>(null);

  if (userLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-surface-2 rounded w-1/3"></div>
            <div className="h-64 bg-surface-2 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!userDetail || !globalId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-surface-1 rounded-lg border border-border p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">User Not Found</h2>
            <p className="text-muted mb-4">The requested user could not be found.</p>
            <Link
              to={adminUsersUrl()}
              className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Back to User List
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { user } = userDetail;

  const handleSelectConnection = (connection: ConnectionSummary) => {
    setSelectedConnection(connection);
  };

  const handleCloseConnectionHistory = () => {
    setSelectedConnection(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted mb-2">
              <Link to={adminUsersUrl()} className="hover:text-foreground">
                Users
              </Link>
              <span>/</span>
              <Link to={adminUserDetailUrl(globalId)} className="hover:text-foreground">
                {user.username}
              </Link>
              <span>/</span>
              <span className="text-foreground">Relationships</span>
            </div>
            <h1 className="text-3xl font-bold text-foreground">
              User Relationships: {user.displayName || user.username}
            </h1>
          </div>
          <Link
            to={adminUserDetailUrl(globalId)}
            className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
          >
            Back to User Details
          </Link>
        </div>

        {/* Main content */}
        <div className="space-y-6">
          {/* Connection History Panel (when a connection is selected) */}
          {selectedConnection && (
            <ConnectionHistory
              userAId={globalId}
              userBId={selectedConnection.userId || undefined}
              userBEmail={selectedConnection.email || undefined}
              onClose={handleCloseConnectionHistory}
            />
          )}

          {/* Relationship Graph Viewer */}
          <RelationshipGraphViewer
            userId={globalId}
            onSelectConnection={handleSelectConnection}
          />
        </div>
      </div>
    </div>
  );
}
